import { useHandler } from "@/hooks";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import * as React from "react";
import { forwardRef, memo } from "react";
import { cn } from "../../lib/utils";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPortal,
  SheetTitle,
} from "./sheet";
import {
  Sidebar as BaseSidebar,
  SidebarContent as BaseSidebarContent,
  SidebarGroup as BaseSidebarGroup,
  SidebarGroupContent as BaseSidebarGroupContent,
  SidebarHeader as BaseSidebarHeader,
  SidebarInset as BaseSidebarInset,
  SidebarMenu as BaseSidebarMenu,
  SidebarMenuButton as BaseSidebarMenuButton,
  SidebarMenuItem as BaseSidebarMenuItem,
  SidebarProvider as BaseSidebarProvider,
  SidebarRail as BaseSidebarRail,
  SidebarTrigger as BaseSidebarTrigger,
  useSidebar,
} from "./sidebar";

// No-animation Sheet components with subtle overlay for better UX
const FpSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "left" | "right" | "top" | "bottom";
  }
>(({ className, children, side = "left", ...props }, ref) => (
  <SheetPortal>
    {/* Add a subtle overlay for better UX - but allow pointer events to pass through to elements outside the sheet */}
    <SheetPrimitive.Overlay className="fixed inset-0 z-40 bg-black/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 pointer-events-none" />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        // Base sheet positioning with higher z-index than overlay
        "fixed z-50 gap-4 p-6 shadow-lg",
        // Remove all animations and transitions
        "transition-none animate-none duration-0",
        // Override all animation states
        "data-[state=open]:animate-none data-[state=closed]:animate-none",
        "data-[state=open]:duration-0 data-[state=closed]:duration-0",
        // Position based on side
        side === "left" && "inset-y-0 left-0 h-full border-r",
        side === "right" && "inset-y-0 right-0 h-full border-l",
        side === "top" && "inset-x-0 top-0 border-b",
        side === "bottom" && "inset-x-0 bottom-0 border-t",
        className,
      )}
      {...props}
    >
      {children}
      {/* Remove the close button since we have our own trigger */}
    </SheetPrimitive.Content>
  </SheetPortal>
));
FpSheetContent.displayName = "FpSheetContent";

// Context for forceSheet state management
interface ForceSheetContextType {
  forceSheet: boolean;
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  toggleSheet: () => void;
}

const ForceSheetContext = React.createContext<ForceSheetContextType>({
  forceSheet: false,
  sheetOpen: false,
  setSheetOpen: () => {},
  toggleSheet: () => {},
});

// Custom hook that manages sheet state independently when forceSheet is active
const useFpSidebar = () => {
  const originalContext = useSidebar();
  const forceSheetContext = React.useContext(ForceSheetContext);

  if (forceSheetContext.forceSheet) {
    // Add keyboard shortcut for forceSheet mode
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === "b" &&
          (event.metaKey || event.ctrlKey) &&
          !event.shiftKey // Prevent ⌘+Shift+B from triggering
        ) {
          event.preventDefault();
          forceSheetContext.toggleSheet();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [forceSheetContext.toggleSheet]);

    // Return modified context for forceSheet mode
    return {
      ...originalContext,
      isMobile: true,
      open: forceSheetContext.sheetOpen,
      setOpen: forceSheetContext.setSheetOpen,
      openMobile: forceSheetContext.sheetOpen,
      setOpenMobile: forceSheetContext.setSheetOpen,
      toggleSidebar: forceSheetContext.toggleSheet,
    };
  }

  return originalContext;
};

// FP-styled sidebar components with no animations and minimal rounding
const FpSidebarProvider = React.forwardRef<
  React.ElementRef<typeof BaseSidebarProvider>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarProvider> & {
    forceSheet?: boolean;
  }
>(({ className, forceSheet = false, children, ...props }, ref) => {
  // Independent state management for forceSheet mode
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const toggleSheet = React.useCallback(() => {
    setSheetOpen((prev) => !prev);
  }, []);

  const wrappedSetSheetOpen = React.useCallback((open: boolean) => {
    setSheetOpen(open);
  }, []);

  const forceSheetValue: ForceSheetContextType = React.useMemo(
    () => ({
      forceSheet,
      sheetOpen,
      setSheetOpen: wrappedSetSheetOpen,
      toggleSheet,
    }),
    [forceSheet, sheetOpen, wrappedSetSheetOpen, toggleSheet],
  );

  return (
    <BaseSidebarProvider ref={ref} className={cn(className)} {...props}>
      <ForceSheetContext.Provider value={forceSheetValue}>
        {children}
      </ForceSheetContext.Provider>
    </BaseSidebarProvider>
  );
});
FpSidebarProvider.displayName = "FpSidebarProvider";

const fpSidebarVariants = cva("border-r rounded-none", {
  variants: {
    colorScheme: {
      default: "",
      background: "[&>[data-sidebar=sidebar]]:!bg-background",
    },
  },
  defaultVariants: {
    colorScheme: "default",
  },
});

const FpSidebar = React.forwardRef<
  React.ElementRef<typeof BaseSidebar>,
  React.ComponentPropsWithoutRef<typeof BaseSidebar> & {
    colorScheme?: "default" | "background";
    forceSheet?: boolean;
  }
>(
  (
    {
      className,
      children,
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      colorScheme = "default",
      forceSheet = false,
      ...props
    },
    ref,
  ) => {
    const { isMobile, openMobile, setOpenMobile } = useFpSidebar();

    const sidebarStyle =
      colorScheme === "background"
        ? ({
            "--sidebar-background": "var(--background)",
          } as React.CSSProperties)
        : undefined;

    if (isMobile || forceSheet) {
      // Use mobile state for consistency with toggleSidebar behavior
      const sheetOpen = openMobile;
      const setSheetOpen = setOpenMobile;

      return (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <FpSheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className={cn(
              "w-[--sidebar-width] p-0 text-sidebar-foreground [&>button]:hidden",
              // Force dark background for the sheet version
              forceSheet
                ? "bg-popover text-popover-foreground"
                : colorScheme === "background"
                  ? "bg-background"
                  : "bg-sidebar",
              // Responsive width adjustments
              "max-w-[calc(100vw-2rem)] sm:max-w-[--sidebar-width]",
            )}
            style={
              {
                "--sidebar-width": "min(18rem, calc(100vw - 2rem))",
                ...sidebarStyle,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              {" "}
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </FpSheetContent>
        </Sheet>
      );
    }

    return (
      <BaseSidebar
        ref={ref}
        className={cn(fpSidebarVariants({ colorScheme }), className)}
        side={side}
        variant={variant}
        collapsible={collapsible}
        style={sidebarStyle}
        {...props}
      >
        {children}
      </BaseSidebar>
    );
  },
);
FpSidebar.displayName = "FpSidebar";

const FpSidebarHeader = React.forwardRef<
  React.ElementRef<typeof BaseSidebarHeader>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarHeader>
>(({ className, ...props }, ref) => (
  <BaseSidebarHeader
    ref={ref}
    className={cn("border-b p-2 rounded-none", className)}
    {...props}
  />
));
FpSidebarHeader.displayName = "FpSidebarHeader";

const FpSidebarContent = React.forwardRef<
  React.ElementRef<typeof BaseSidebarContent>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarContent>
>(({ className, ...props }, ref) => (
  <BaseSidebarContent ref={ref} className={cn(className)} {...props} />
));
FpSidebarContent.displayName = "FpSidebarContent";

const FpSidebarGroup = React.forwardRef<
  React.ElementRef<typeof BaseSidebarGroup>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarGroup>
>(({ className, ...props }, ref) => (
  <BaseSidebarGroup ref={ref} className={cn(className)} {...props} />
));
FpSidebarGroup.displayName = "FpSidebarGroup";

const FpSidebarGroupContent = React.forwardRef<
  React.ElementRef<typeof BaseSidebarGroupContent>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarGroupContent>
>(({ className, ...props }, ref) => (
  <BaseSidebarGroupContent ref={ref} className={cn("", className)} {...props} />
));
FpSidebarGroupContent.displayName = "FpSidebarGroupContent";

const FpSidebarMenu = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenu>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenu>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenu ref={ref} className={cn("gap-1", className)} {...props} />
));
FpSidebarMenu.displayName = "FpSidebarMenu";

const FpSidebarMenuItem = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenuItem>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenuItem>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenuItem ref={ref} className={cn("", className)} {...props} />
));
FpSidebarMenuItem.displayName = "FpSidebarMenuItem";

const FpSidebarMenuButton = React.forwardRef<
  React.ElementRef<typeof BaseSidebarMenuButton>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarMenuButton>
>(({ className, ...props }, ref) => (
  <BaseSidebarMenuButton
    ref={ref}
    className={cn("rounded-none", className)}
    {...props}
  />
));
FpSidebarMenuButton.displayName = "FpSidebarMenuButton";

const FpSidebarInset = React.forwardRef<
  React.ElementRef<typeof BaseSidebarInset>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarInset>
>(({ className, ...props }, ref) => (
  <BaseSidebarInset
    ref={ref}
    className={cn("rounded-none", className)}
    {...props}
  />
));
FpSidebarInset.displayName = "FpSidebarInset";

const FpSidebarRail = React.forwardRef<
  React.ElementRef<typeof BaseSidebarRail>,
  React.ComponentPropsWithoutRef<typeof BaseSidebarRail>
>(({ className, ...props }, ref) => (
  <BaseSidebarRail ref={ref} className={cn("", className)} {...props} />
));
FpSidebarRail.displayName = "FpSidebarRail";

const FpSidebarTrigger = memo(
  forwardRef<
    React.ComponentRef<typeof BaseSidebarTrigger>,
    React.ComponentPropsWithoutRef<typeof BaseSidebarTrigger>
  >(function FpSidebarTrigger({ className, onClick, ...props }, ref) {
    const { toggleSidebar } = useFpSidebar();

    const handleClick = useHandler(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        toggleSidebar();
        onClick?.(event);
      },
    );

    return (
      <BaseSidebarTrigger
        ref={ref}
        className={cn(
          "rounded-md w-7 h-7 flex-shrink-0 relative z-50 pointer-events-auto",
          className,
        )}
        onClick={handleClick}
        {...props}
      />
    );
  }),
);

export {
  FpSidebar,
  FpSidebarContent,
  FpSidebarGroup,
  FpSidebarGroupContent,
  FpSidebarHeader,
  FpSidebarInset,
  FpSidebarMenu,
  FpSidebarMenuItem,
  FpSidebarMenuButton,
  FpSidebarProvider,
  FpSidebarRail,
  FpSidebarTrigger,
  useFpSidebar,
};
